import { THEME_STORAGE_KEY } from "@/config";
import { create } from "zustand";

export type ThemeMode = "light" | "dark";

type ThemeUpdater = ThemeMode | ((currentTheme: ThemeMode) => ThemeMode);

type ThemeStore = {
    themeMode: ThemeMode;
    setThemeMode: (nextTheme: ThemeUpdater) => void;
    toggleTheme: () => void;
};

const getSystemTheme = (): ThemeMode => {
    if (typeof window === "undefined") {
        return "light";
    }

    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getInitialTheme = (): ThemeMode => {
    if (typeof window === "undefined") {
        return "light";
    }

    try {
        const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

        if (storedTheme === '"light"' || storedTheme === '"dark"') {
            return JSON.parse(storedTheme) as ThemeMode;
        }
    } catch (error) {
        console.warn(`Error reading localStorage key "${THEME_STORAGE_KEY}":`, error);
    }

    return getSystemTheme();
};

const persistTheme = (themeMode: ThemeMode) => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(themeMode));
    } catch (error) {
        console.warn(`Error setting localStorage key "${THEME_STORAGE_KEY}":`, error);
    }
};

export const useTheme = create<ThemeStore>((set) => ({
    themeMode: getInitialTheme(),
    setThemeMode: (nextTheme) =>
        set((state) => {
            const themeMode = nextTheme instanceof Function ? nextTheme(state.themeMode) : nextTheme;
            persistTheme(themeMode);
            return { themeMode };
        }),
    toggleTheme: () =>
        set((state) => {
            const themeMode = state.themeMode === "dark" ? "light" : "dark";
            persistTheme(themeMode);
            return { themeMode };
        }),
}));
